import React from 'react';
import PropTypes from 'prop-types';
import { LuAlertTriangle, LuDownload, LuExternalLink } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from 'components/common';
import { RUNNING, SEVERITY, standardLabel, when } from './compareFormat';
import FindingList from './FindingList';

// One comparison's result on the Draft Check page: which standard, the online view
// and download of the marked-up file, progress while it runs, then summary + findings.

const TONES = {
  amber: 'tw-bg-amber-50 tw-border-amber-200 tw-text-amber-800', sky: 'tw-bg-sky-50 tw-border-sky-200 tw-text-sky-800',
  slate: 'tw-bg-slate-50 tw-border-slate-200 tw-text-slate-700', red: 'tw-bg-red-50 tw-border-red-200 tw-text-red-800',
};
export const Notice = ({ tone, children }) => (
  <p role={tone === 'red' ? 'alert' : 'note'}
    className={`tw-m-0 tw-flex tw-items-start tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-lg tw-border tw-border-solid tw-text-[12px] ${TONES[tone]}`}>
    <LuAlertTriangle size={13} className="tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </p>
);
Notice.propTypes = { tone: PropTypes.oneOf(Object.keys(TONES)).isRequired, children: PropTypes.node };

const REVIEW_LEVELS = ['high', 'medium', 'low', 'review'];

const Summary = ({ comparison }) => {
  const s = comparison.stats || {};
  const counts = s.by_severity || {};
  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
        {REVIEW_LEVELS.filter((k) => counts[k]).map((k) => (
          <span key={k} className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-border tw-border-solid tw-text-[12px] tw-font-semibold ${SEVERITY[k].cls}`}>
            {counts[k]} {SEVERITY[k].label.toLowerCase()}
          </span>
        ))}
        {!REVIEW_LEVELS.some((k) => counts[k]) && (
          <span className="tw-text-[13px] tw-font-semibold tw-text-emerald-700">No difference that needs review</span>
        )}
        <span className="tw-text-[12px] tw-text-slate-500">
          · {counts.none || 0} fill-in / formatting · {Math.round((s.similarity || 0) * 100)}% the same as the standard
        </span>
      </div>
      {s.wrong_template_suspected && (
        <Notice tone="amber">The files have little in common — is this the right standard (language / Contract vs Proposal)?</Notice>
      )}
      {comparison.ai_status === 'failed' && (
        <Notice tone="sky">AI review was not available, so differences are listed as &quot;to review&quot; without a verdict.</Notice>
      )}
      {s.scanned && <Notice tone="slate">The draft is a scan: its text was read by OCR, so a few differences may be reading errors.</Notice>}
      {s.annotate_error && <Notice tone="amber">The annotated file could not be made: {s.annotate_error}</Notice>}
    </div>
  );
};
Summary.propTypes = { comparison: PropTypes.object.isRequired };

const ComparisonResult = ({ comparison }) => {
  const running = RUNNING.includes(comparison.status);
  return (
    <section aria-label="Result" className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-[12px] tw-text-slate-500">
        <span>
          vs {standardLabel(comparison.template)} · {when(comparison.created_at)}
          {comparison.created_by ? ` · ${comparison.created_by}` : ''}
        </span>
        {comparison.annotated_url && (
          <Link to={`/crm/document-comparisons/${comparison.id}`} target="_blank" rel="noopener"
            className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-primary-200 tw-bg-white tw-text-primary-700 tw-text-[12px] tw-font-semibold hover:tw-bg-primary-50 tw-no-underline">
            <LuExternalLink size={13} aria-hidden="true" /> View online
          </Link>
        )}
        {comparison.annotated_url && (
          <a href={comparison.annotated_url} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded-lg tw-bg-primary-600 tw-text-white tw-text-[12px] tw-font-semibold hover:tw-bg-primary-700 tw-no-underline">
            <LuDownload size={13} aria-hidden="true" /> Download marked-up {comparison.annotated_filename?.split('.').pop()?.toUpperCase()}
          </a>
        )}
      </div>
      {running && (
        <div role="status" className="tw-flex tw-items-center tw-gap-2 tw-py-6 tw-justify-center tw-text-sm tw-text-slate-600">
          <LoadingSpinner size="sm" /> Comparing… a scanned PDF can take a few minutes.
        </div>
      )}
      {comparison.status === 'failed' && <Notice tone="red">{comparison.error || 'The comparison failed.'}</Notice>}
      {comparison.status === 'done' && (
        <>
          <Summary comparison={comparison} />
          <FindingList findings={comparison.findings || []} />
        </>
      )}
    </section>
  );
};
ComparisonResult.propTypes = { comparison: PropTypes.object.isRequired };

export default ComparisonResult;
